import type { JSX } from 'react'
import type { ButtonColors, IButtonProps } from 'atoms/Button'
import type { COLOR } from 'vars/color'
import type { ActionButtonColors, ButtonBorderColors } from './ActionButton.h'

import React from 'react'
import { Icon, IconSizes } from 'atoms/Icon'
import { TextBox, TextBoxTypes } from 'atoms/TextBox'
import { ButtonCorners, ButtonFonts, IconPositions } from './ActionButton.h'
import { ActionButtonSC } from './ActionButton.styled'

export interface IActionButtonProps extends IButtonProps {
  selected?: boolean
  dataTestId?: string
  backgroundColorSelectedDefault?: COLOR
  backgroundColorSelectedHovered?: COLOR
  backgroundColorSelectedPressed?: COLOR
  bordersColorSelectedDefault?: ButtonBorderColors
  bordersColorSelectedHovered?: ButtonBorderColors
  bordersColorSelectedPressed?: ButtonBorderColors
  colorSelectedDefault?: ButtonColors | ActionButtonColors
  colorSelectedHovered?: ButtonColors | ActionButtonColors
  colorSelectedPressed?: ButtonColors | ActionButtonColors
}

export const ActionButton = ({
  type = 'button',
  font = ButtonFonts.Button1,
  title,
  icon,
  iconPosition,
  iconSize,
  colorDefault,
  colorHovered,
  colorPressed,
  colorDisabled,
  backgroundColorDefault,
  backgroundColorHovered,
  backgroundColorPressed,
  backgroundColorDisabled,
  backgroundColorSelectedPressed,
  backgroundColorSelectedDefault,
  backgroundColorSelectedHovered,
  gradient,
  paddings,
  corners = ButtonCorners.CORNERS_0,
  borderWidth,
  bordersColorDefault,
  bordersColorHovered,
  bordersColorPressed,
  bordersColorDisabled,
  fluid,
  className,
  disabled,
  selected,
  colorSelectedDefault,
  colorSelectedHovered,
  colorSelectedPressed,
  bordersColorSelectedDefault,
  bordersColorSelectedHovered,
  bordersColorSelectedPressed,
  onClick,
  tabIndex,
  dataTestId = '',
}: IActionButtonProps): JSX.Element => {
  // With `selected` supplied the state belongs to the caller: flipping a local
  // copy on click makes the button show the outcome of a request that has not
  // happened yet, and leaves it lying when the request fails or when the row is
  // re-ordered and this instance is reused for another entity.
  const isControlled = selected !== undefined
  const [ownSelected, setOwnSelected] = React.useState<boolean>(false)
  const isSelected = isControlled ? selected : ownSelected

  const toggleSelected = (event: React.MouseEvent): void => {
    event.stopPropagation()
    if (!isControlled) setOwnSelected((prev) => !prev)
    onClick?.(event)
  }

  return (
    <ActionButtonSC
      type={type}
      disabled={disabled}
      selected={isSelected}
      paddings={paddings}
      gradient={gradient}
      corners={corners}
      borderWidth={borderWidth}
      bordersColorDefault={bordersColorDefault}
      bordersColorHovered={bordersColorHovered || bordersColorDefault}
      bordersColorSelectedPressed={
        bordersColorSelectedPressed ||
        bordersColorPressed ||
        bordersColorDefault
      }
      bordersColorSelectedDefault={
        bordersColorSelectedDefault || bordersColorDefault
      }
      bordersColorSelectedHovered={
        bordersColorSelectedHovered ||
        bordersColorHovered ||
        bordersColorDefault
      }
      bordersColorPressed={bordersColorPressed || bordersColorDefault}
      bordersColorDisabled={bordersColorDisabled}
      className={className!}
      fluid={fluid}
      iconOrder={iconPosition === IconPositions.Left ? 1 : 2}
      colorDefault={colorDefault}
      colorHovered={colorHovered || colorDefault}
      colorPressed={colorPressed || colorDefault}
      colorSelectedDefault={colorSelectedDefault || colorDefault}
      colorSelectedHovered={
        colorSelectedHovered || colorHovered || colorDefault
      }
      colorSelectedPressed={
        colorSelectedPressed || colorPressed || colorDefault
      }
      colorDisabled={colorDisabled}
      backgroundColorDefault={backgroundColorDefault}
      backgroundColorHovered={backgroundColorHovered || backgroundColorDefault}
      backgroundColorPressed={backgroundColorPressed || backgroundColorDefault}
      backgroundColorSelectedDefault={
        backgroundColorSelectedDefault || backgroundColorDefault
      }
      backgroundColorSelectedHovered={
        backgroundColorSelectedHovered ||
        backgroundColorHovered ||
        backgroundColorDefault
      }
      backgroundColorSelectedPressed={
        backgroundColorSelectedPressed ||
        backgroundColorPressed ||
        backgroundColorDefault
      }
      backgroundColorDisabled={backgroundColorDisabled}
      onClick={toggleSelected}
      font={TextBoxTypes[font]}
      tabIndex={tabIndex}
      data-testid={dataTestId}
    >
      {icon && (
        <Icon
          name={icon}
          size={iconSize ? IconSizes[iconSize] : null}
          filled={isSelected}
        />
      )}
      {title && <TextBox title={title} maxLines={1} />}
    </ActionButtonSC>
  )
}

ActionButton.displayName = 'ActionButton'
